import { useEffect, useMemo, useState } from 'react';

import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { getVenueLinks } from 'modules/shared-menu/api';

import { SharedMenu, OfferingPlatformScope } from '../types';

type Props = {
  venues: Venue[] | undefined;
  sharedMenus: SharedMenu[] | undefined;
  currentScopeType: OfferingPlatformScope['type'];
};

type SharedMenuLink =
  | { scope: OfferingPlatformScope['type']; sharedMenuName?: string; sharedMenuId?: string }
  | { scope: 'none' };

export type VenueWithSharedMenuLinkInfo = Venue & {
  sharedMenuLink: SharedMenuLink;
};

const otherScopeMap: { [key in OfferingPlatformScope['type']]: OfferingPlatformScope['type'] } = {
  brand: 'merchant',
  merchant: 'brand',
  venue: 'venue',
  selection: 'selection',
};

const useVenuesWithSelectionLinks = ({ venues, sharedMenus, currentScopeType }: Props) => {
  const [error, setError] = useState<any>(undefined);
  const [loading, setLoading] = useState(true);
  const [venuesUsingOtherScopeSharedMenu, setVenuesUsingOtherScopeSharedMenu] = useState<VenueWithSharedMenuLinkInfo[]>(
    [],
  );
  const [venuesNotUsingAnySharedMenu, setVenuesNotUsingAnySharedMenu] = useState<VenueWithSharedMenuLinkInfo[]>([]);
  const [venuesUsingCurrentScopeSharedMenu, setVenuesUsingCurrentScopeSharedMenu] = useState<
    VenueWithSharedMenuLinkInfo[]
  >([]);

  useEffect(() => {
    const getVenueLinksForVenuesNotUsingCurrentScopeSharedMenu = async (venuesNotUsingCurrentScope: Venue[]) => {
      try {
        const venueLinks = await getVenueLinks(venuesNotUsingCurrentScope.map(venue => venue.id));
        setVenuesUsingOtherScopeSharedMenu(
          venuesNotUsingCurrentScope
            .filter(venue => venueLinks.venue_links.some(link => link.venue_id === venue.id))
            .map(venue => ({ ...venue, sharedMenuLink: { scope: otherScopeMap[currentScopeType] } })),
        );
        setVenuesNotUsingAnySharedMenu(
          venuesNotUsingCurrentScope
            .filter(venue => !venueLinks.venue_links.some(link => link.venue_id === venue.id))
            .map(venue => ({ ...venue, sharedMenuLink: { scope: 'none' } })),
        );
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    if (sharedMenus === undefined || venues === undefined) {
      return;
    }

    setVenuesUsingCurrentScopeSharedMenu(
      venues
        .map(venue => {
          const linkedSharedMenu = sharedMenus?.find(sharedMenu =>
            sharedMenu.linked_venue_ids ? sharedMenu.linked_venue_ids.includes(venue.id) : false,
          );
          if (linkedSharedMenu) {
            const sharedMenuLink: SharedMenuLink = {
              scope: currentScopeType,
              sharedMenuName: linkedSharedMenu.name[0].value,
              sharedMenuId: linkedSharedMenu.id,
            };
            return {
              ...venue,
              sharedMenuLink,
            };
          }
          return venue;
        })
        .filter((venue: any): venue is VenueWithSharedMenuLinkInfo => venue.sharedMenuLink !== undefined),
    );

    const allLinkedVenueIds = sharedMenus.flatMap(menu => menu.linked_venue_ids);
    const venuesNotUsingSharedMenusFromCurrentScope = venues.filter(venue => !allLinkedVenueIds.includes(venue.id));

    if (venuesNotUsingSharedMenusFromCurrentScope.length > 0) {
      getVenueLinksForVenuesNotUsingCurrentScopeSharedMenu(venuesNotUsingSharedMenusFromCurrentScope);
    }

    const allVenuesUseCurrentScopedSharedMenu = venuesNotUsingSharedMenusFromCurrentScope.length === 0;
    if (allVenuesUseCurrentScopedSharedMenu) {
      setLoading(false);
      setVenuesNotUsingAnySharedMenu([]);
      setVenuesUsingOtherScopeSharedMenu([]);
    }
  }, [sharedMenus, venues, currentScopeType]);

  const venuesWithSelectionLinks = useMemo(
    () => [...venuesUsingCurrentScopeSharedMenu, ...venuesUsingOtherScopeSharedMenu, ...venuesNotUsingAnySharedMenu],
    [venuesNotUsingAnySharedMenu, venuesUsingCurrentScopeSharedMenu, venuesUsingOtherScopeSharedMenu],
  );

  return {
    venues: venuesWithSelectionLinks,
    error,
    loading,
  };
};

export default useVenuesWithSelectionLinks;
